// Local preview adapters. Business kind + day-one identify demo fixtures, NOT tenants.
// No provider, tax, authentication or device synchronization contract is implemented here.
export function previewCents(input) {
  const value=String(input??'').trim();
  if(!/^\d+(?:[.,]\d{1,2})?$/.test(value)) return null;
  const [whole,fraction='']=value.replace(',','.').split('.');
  const cents=Number(whole)*100+Number(fraction.padEnd(2,'0'));
  return Number.isSafeInteger(cents)?cents:null;
}
// Explicit year only: the original short absence dates do not identify a year.
export function previewDate(input) {
  const value=String(input??'').trim(),iso=value.match(/^(\d{4})-(\d{2})-(\d{2})$/),de=value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if(!iso&&!de)return null;
  const [year,month,day]=iso?[+iso[1],+iso[2],+iso[3]]:[+de[3],+de[2],+de[1]],date=new Date(year,month-1,day);
  return date.getFullYear()===year&&date.getMonth()===month-1&&date.getDate()===day?`${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`:null;
}
export function createScopedStorage(storage, scope, report=()=>{}) {
  const prefix='zalso.preview.bo.v1.'+scope+'.';
  const damaged=new Set();
  const arrays=/^(zalso-bo2-links|zalso-bo2-taxok|zalso\.printers\.)/;
  const objects=/^(zalso\.(settings|chcfg|channels|datev|inventur|platforms|platcases)|zalso-floor|zalso-cash-reasons|zalso-bo2-conn)/;
  const valid=(key,value)=>{
    if(!arrays.test(key)&&!objects.test(key)) return true;
    const v=JSON.parse(value);
    if(arrays.test(key)) return Array.isArray(v) && (key.startsWith('zalso-bo2-taxok') ? v.every(x=>typeof x==='string') : v.every(x=>x&&typeof x==='object'&&typeof x.id==='string'&&(key.startsWith('zalso-bo2-links')?typeof x.t==='string'&&typeof x.st==='string'&&Number.isSafeInteger(x.amt):typeof x.name==='string')));
    if(!v||typeof v!=='object'||Array.isArray(v)) return false;
    if(key==='zalso-floor') return Array.isArray(v.rooms)&&v.rooms.length>0&&v.rooms.every(r=>r&&typeof r.name==='string'&&Array.isArray(r.tables)&&r.tables.every(t=>t&&typeof t.id==='string'&&typeof t.name==='string'&&Number.isFinite(t.seats)));
    if(key.startsWith('zalso.chcfg.')&&v.pays!==undefined&&!Array.isArray(v.pays))return false;
    if(key==='zalso-cash-reasons')return ['ein','aus'].every(k=>Array.isArray(v[k])&&v[k].every(r=>r&&typeof r.label==='string'&&typeof r.on==='boolean'));
    if(key.startsWith('zalso.platforms.')&&v.p!==undefined)return v.p&&typeof v.p==='object'&&!Array.isArray(v.p)&&Object.values(v.p).every(p=>p&&typeof p==='object');
    return true;
  };
  return {
    key:key=>prefix+key,
    getItem(key) {
      try { const value=storage.getItem(prefix+key); if(value!==null&&!valid(key,value)) throw Error('Ungültige Datenstruktur'); return value; }
      catch(error) { damaged.add(key); report('Gespeicherte Vorschau kann nicht geladen werden ('+key+'). Der vorhandene Stand bleibt geschützt.'); throw error; }
    },
    setItem(key,value) {
      try {
        if(damaged.has(key)) throw Error('Beschädigter Bestand darf nicht überschrieben werden');
        if(!valid(key,String(value))) throw Error('Ungültige Datenstruktur');
        storage.setItem(prefix+key,String(value));
      } catch(error) { report('Nicht gespeichert ('+key+'): '+error.message); throw error; }
    },
    removeItem(key) { try{storage.removeItem(prefix+key);}catch(error){report('Nicht entfernt: '+error.message);throw error;} },
  };
}
export function createPreviewSource(storage, report=()=>{}) {
  const values=new Map(), listeners=new Set(), damaged=new Set();
  const clone=value=>JSON.parse(JSON.stringify(value));
  const check=(value,seed,key)=>{
    if(key.endsWith(':catalog'))return value&&Array.isArray(value.cats)&&Array.isArray(value.arts)&&value.cats.every(c=>c&&typeof c.id==='string'&&typeof c.n==='string')&&value.arts.every(a=>a&&typeof a.id==='string'&&typeof a.n==='string'&&typeof a.cat==='string'&&Number.isSafeInteger(a.p));
    if(key.endsWith(':vouchers'))return value&&Array.isArray(value.list)&&value.list.every(v=>v&&typeof v.code==='string'&&Array.isArray(v.hist)&&Number.isSafeInteger(v.rest)&&Number.isSafeInteger(v.value));
    if(Array.isArray(seed)) return Array.isArray(value)&&value.every(x=>x&&typeof x==='object'&&('id' in x));
    return value&&typeof value==='object'&&!Array.isArray(value)&&Object.keys(seed).every(k=>Array.isArray(seed[k])?Array.isArray(value[k]):typeof value[k]===typeof seed[k]);
  };
  return {
    subscribe(fn){listeners.add(fn);return ()=>listeners.delete(fn);},
    read(key,initial) {
      if(!values.has(key)) {
        const seed=typeof initial==='function'?initial():initial;
        let value=clone(seed);
        try { const raw=storage.getItem('records:'+key); if(raw!==null){const parsed=JSON.parse(raw);if(!check(parsed,seed,key))throw Error('Ungültige Datenstruktur');value=parsed;} }
        catch(error){damaged.add(key);report('Vorschaudaten für '+key+' beschädigt oder nicht lesbar. Ersatzansicht nur lesend; gespeicherter Bestand bleibt erhalten.');}
        values.set(key,value);
      }
      return values.get(key);
    },
    update(key,change,initial) {
      const before=this.read(key,initial);
      if(damaged.has(key)){report('Nicht gespeichert: beschädigter Bestand '+key+'.');return false;}
      const next=typeof change==='function'?change(clone(before)):change;
      try {if(!check(next,before,key))throw Error('Ungültige Datenstruktur'); storage.setItem('records:'+key,JSON.stringify(next)); }
      catch(error){report('Nicht gespeichert: '+key+' · '+error.message);return false;}
      values.set(key,next); listeners.forEach(fn=>fn()); return true;
    },
  };
}
export function parsePreviewCsv(text) {
  const source=text.replace(/^\uFEFF/,'');
  const first=source.split(/\r?\n/,1)[0];
  const delimiter=first.includes(';')?';':',';
  const rows=[];let row=[],cell='',quote=false;
  for(let i=0;i<source.length;i++){
    const c=source[i];
    if(c==='"'){if(quote&&source[i+1]==='"'){cell+='"';i++;}else quote=!quote;}
    else if(c===delimiter&&!quote){row.push(cell);cell='';}
    else if((c==='\n'||c==='\r')&&!quote){if(c==='\r'&&source[i+1]==='\n')i++;row.push(cell);if(row.some(x=>x.trim()))rows.push(row);row=[];cell='';}
    else cell+=c;
  }
  if(quote)throw Error('Nicht geschlossene Anführungszeichen in CSV.');
  row.push(cell);if(row.some(x=>x.trim()))rows.push(row);
  if(rows.length<2)throw Error('CSV benötigt Spaltenüberschriften und mindestens eine Datenzeile.');
  if(rows.some(r=>r.length!==rows[0].length))throw Error('CSV-Zeilen haben unterschiedliche Spaltenzahlen.');
  return {cols:rows[0],rows:rows.slice(1)};
}
